import AppIcon from '../components/AppIcon';
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const TABS = [
  { key: 'overview', label: 'Overview', icon: 'overview' },
  { key: 'memories', label: 'Memories', icon: 'image' },
  { key: 'family', label: 'Family', icon: 'family' },
  { key: 'edit', label: 'Edit', icon: 'contributor' },
];

// Demo member data
const DEMO_MEMBER = {
  id: '1',
  firstName: 'Kao',
  lastName: 'Saetern',
  spiritualName: '',
  adultName: '',
  gender: 'MALE',
  dateOfBirth: '1985-03-15',
  dateOfDeath: null,
  placeOfBirth: 'Sacramento, CA',
  isLiving: true,
  kinshipTerm: 'Self',
  photo: null,
  memories: [
    { id: '1', title: 'Family Reunion 2024', type: 'PHOTO', date: '2024-07-15' },
    { id: '2', title: 'Grandpa\'s Stories', type: 'STORY', date: '2023-12-25' },
  ],
  family: {
    parents: [
      { id: '2', name: 'Father Saetern', gender: 'MALE', isLiving: true },
      { id: '5', name: 'Mother Saetern', gender: 'FEMALE', isLiving: true },
    ],
    siblings: [
      { id: '8', name: 'Sister Saetern', gender: 'FEMALE', isLiving: true },
    ],
    spouse: null,
    children: [],
  },
};

function MemberPage() {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState('overview');
  const [editForm, setEditForm] = useState({
    firstName: DEMO_MEMBER.firstName,
    lastName: DEMO_MEMBER.lastName,
    spiritualName: DEMO_MEMBER.spiritualName,
    adultName: DEMO_MEMBER.adultName,
    gender: DEMO_MEMBER.gender,
    dateOfBirth: DEMO_MEMBER.dateOfBirth,
    placeOfBirth: DEMO_MEMBER.placeOfBirth,
  });
  const [editErrors, setEditErrors] = useState({});

  const member = DEMO_MEMBER; // Will be replaced with GraphQL query
  const hasEditPermission = true; // Will be replaced with permission check

  const handleEditChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
    setEditErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    const errors = {};
    if (!editForm.firstName.trim()) errors.firstName = 'First name is required';
    if (!editForm.lastName.trim()) errors.lastName = 'Last name is required';
    if (Object.keys(errors).length > 0) {
      setEditErrors(errors);
      return;
    }
    // TODO: GraphQL mutation
  };

  const getInitials = (name) => {
    return name.split(' ').map((n) => n[0]).join('').toUpperCase();
  };

  return (
    <div className="">

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Profile Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-20 h-20 rounded-full bg-indigo-100 flex items-center justify-center text-2xl font-bold text-indigo-600 shrink-0">
              {getInitials(`${member.firstName} ${member.lastName}`)}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold text-gray-900">{member.firstName} {member.lastName}</h2>
              {member.spiritualName && (
                <p className="text-sm text-gray-500">Spiritual name: {member.spiritualName}</p>
              )}
              {member.adultName && (
                <p className="text-sm text-gray-500">Adult name: {member.adultName}</p>
              )}
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  member.isLiving ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {member.isLiving ? 'Living' : 'Deceased'}
                </span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {member.gender === 'MALE' ? 'Male' : 'Female'}
                </span>
                {member.kinshipTerm && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                    {member.kinshipTerm}
                  </span>
                )}
              </div>
            </div>
            <div className="hidden sm:flex gap-2">
              <a href={`/memories/new?member=${member.id}`} className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors">
                + Memory
              </a>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex gap-1 -mb-px overflow-x-auto">
            {TABS.filter((t) => t.key !== 'edit' || hasEditPermission).map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                  activeTab === tab.key
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <AppIcon name={tab.icon} size={16} />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <OverviewTab member={member} />
        )}
        {activeTab === 'memories' && (
          <MemoriesTab member={member} />
        )}
        {activeTab === 'family' && (
          <FamilyTab member={member} />
        )}
        {activeTab === 'edit' && hasEditPermission && (
          <EditTab
            form={editForm}
            errors={editErrors}
            onChange={handleEditChange}
            onSubmit={handleEditSubmit}
          />
        )}
      </main>
    </div>
  );
}

function OverviewTab({ member }) {
  const details = [
    { label: 'Date of Birth', value: member.dateOfBirth },
    { label: 'Date of Death', value: member.dateOfDeath || '—' },
    { label: 'Place of Birth', value: member.placeOfBirth },
    { label: 'Kinship Term', value: member.kinshipTerm },
  ].filter((d) => d.value && d.value !== '—');

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Details</h3>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {details.map(({ label, value }) => (
            <div key={label}>
              <dt className="text-sm text-gray-500">{label}</dt>
              <dd className="text-sm font-medium text-gray-900">{value}</dd>
            </div>
          ))}
        </dl>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {[
          { label: 'Add Memory', icon: 'image', href: `/memories/new?member=${member.id}` },
          { label: 'View Tree', icon: 'family-tree', href: '/family-tree' },
          { label: 'Invite Family', icon: 'mail', href: '/invitations/new' },
        ].map((action) => (
          <a key={action.label} href={action.href} className="bg-white rounded-xl shadow-sm p-4 border border-gray-100 hover:border-indigo-200 hover:shadow-md transition-all text-center">
            <AppIcon name={action.icon} size={24} />
            <p className="mt-2 text-sm font-medium text-gray-700">{action.label}</p>
          </a>
        ))}
      </div>
    </div>
  );
}

function MemoriesTab({ member }) {
  const [filter, setFilter] = useState('ALL');
  const types = ['ALL', 'PHOTO', 'STORY', 'AUDIO', 'VIDEO'];

  const memories = member.memories || [];

  return (
    <div>
      <div className="flex items-center gap-2 mb-4 overflow-x-auto">
        {types.map((t) => (
          <button
            key={t}
            onClick={() => setFilter(t)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              filter === t ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {t === 'ALL' ? 'All' : t}
          </button>
        ))}
      </div>

      {memories.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
          <AppIcon name="camera" size={36} />
          <h3 className="mt-3 text-lg font-semibold text-gray-900">No memories yet</h3>
          <p className="mt-1 text-sm text-gray-500">Add the first memory for this member.</p>
          <a href={`/memories/new?member=${member.id}`} className="inline-block mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors">
            + Add Memory
          </a>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {memories.map((mem) => (
            <a key={mem.id} href={`/memories/${mem.id}`} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:border-indigo-200 hover:shadow-md transition-all">
              <div className="flex items-start gap-3">
                <AppIcon name={mem.type === 'PHOTO' ? 'photo' : mem.type === 'STORY' ? 'story' : mem.type === 'AUDIO' ? 'audio' : 'video'} size={24} />
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">{mem.title}</h4>
                  <p className="text-xs text-gray-500">{mem.date}</p>
                </div>
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function FamilyTab({ member }) {
  const { family } = member;

  const renderFamilyGroup = (title, members, icon) => {
    if (!members || members.length === 0) return null;
    return (
      <div className="mb-6">
        <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1"><AppIcon name={icon} size={14} /> {title}</h4>
        <div className="space-y-2">
          {members.map((m) => (
            <a key={m.id} href={`/members/${m.id}`} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100 hover:border-indigo-200 transition-all">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                m.gender === 'MALE' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'
              }`}>
                {m.name.split(' ').map((n) => n[0]).join('')}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{m.name}</p>
                <p className="text-xs text-gray-500">
                  {m.isLiving ? 'Living' : 'Deceased'} · {m.gender === 'MALE' ? 'Male' : 'Female'}
                </p>
              </div>
              {!m.isLiving && (
                <span className="ml-auto text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">Deceased</span>
              )}
            </a>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      {renderFamilyGroup('Parents', family.parents, 'parents')}
      {renderFamilyGroup('Siblings', family.siblings, 'users')}
      {family.spouse && renderFamilyGroup('Spouse', [family.spouse], 'mail')}
      {renderFamilyGroup('Children', family.children, 'users')}

      {!family.parents?.length && !family.siblings?.length && !family.spouse && !family.children?.length && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
          <AppIcon name="family" size={36} className="text-gray-400" />
          <h3 className="mt-3 text-lg font-semibold text-gray-900">No family connections yet</h3>
          <p className="mt-1 text-sm text-gray-500">Add family members to build the family tree.</p>
        </div>
      )}
    </div>
  );
}

function EditTab({ form, errors, onChange, onSubmit }) {
  return (
    <form onSubmit={onSubmit} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">Edit Profile</h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
          <input
            type="text"
            value={form.firstName}
            onChange={(e) => onChange('firstName', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
              errors.firstName ? 'border-red-300' : 'border-gray-300'
            }`}
          />
          {errors.firstName && <p className="text-xs text-red-600 mt-1">{errors.firstName}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
          <input
            type="text"
            value={form.lastName}
            onChange={(e) => onChange('lastName', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
              errors.lastName ? 'border-red-300' : 'border-gray-300'
            }`}
          />
          {errors.lastName && <p className="text-xs text-red-600 mt-1">{errors.lastName}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Spiritual Name</label>
          <input
            type="text"
            value={form.spiritualName}
            onChange={(e) => onChange('spiritualName', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Adult Name</label>
          <input
            type="text"
            value={form.adultName}
            onChange={(e) => onChange('adultName', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
          <select
            value={form.gender}
            onChange={(e) => onChange('gender', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="MALE">Male</option>
            <option value="FEMALE">Female</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
          <input
            type="date"
            value={form.dateOfBirth}
            onChange={(e) => onChange('dateOfBirth', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>

        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Place of Birth</label>
          <input
            type="text"
            value={form.placeOfBirth}
            onChange={(e) => onChange('placeOfBirth', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
      </div>

      <div className="flex gap-3 mt-6 pt-6 border-t border-gray-200">
        <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors">
          Save Changes
        </button>
        <button type="button" className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
          Cancel
        </button>
      </div>
    </form>
  );
}

export default MemberPage;